"use client";

import * as React from "react";
import { StudyUserLimits } from "../types";

/* -------------------------
   Derived helpers
------------------------- */

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round(bytes / Math.pow(k, i))} ${sizes[i]}`;
}

/* -------------------------
   Validation builders
------------------------- */

function buildValidators(limits: StudyUserLimits) {
  const allowedMimes = new Set(limits.supportedTypes.map((t) => t.mime));
  const allowedExtensions = new Set(
    limits.supportedTypes.flatMap((t) =>
      t.extensions.map((e) => `.${e.toLowerCase()}`)
    )
  );

  return {
    fileSize(size: number) {
      if (size > limits.maxFileSize) {
        return {
          valid: false,
          error: `File exceeds ${formatFileSize(limits.maxFileSize)} limit`,
        };
      }
      return { valid: true };
    },

    fileType(type: string, filename?: string) {
      const mimeValid = allowedMimes.has(type);

      const extValid =
        !filename ||
        Array.from(allowedExtensions).some((ext) =>
          filename.toLowerCase().endsWith(ext)
        );

      if (!mimeValid || !extValid) {
        return {
          valid: false,
          error: `File type not supported`,
        };
      }

      return { valid: true };
    },

    file(file: File) {
      const sizeCheck = this.fileSize(file.size);
      if (!sizeCheck.valid) return sizeCheck;

      const typeCheck = this.fileType(file.type, file.name);
      if (!typeCheck.valid) return typeCheck;

      return { valid: true };
    },
  };
}

/* -------------------------
   Context types
------------------------- */

type UserLimitsContextValue = {
  limits: StudyUserLimits;
  validators: ReturnType<typeof buildValidators>;
  formatFileSize: (bytes: number) => string;
  setLimits: React.Dispatch<React.SetStateAction<StudyUserLimits>>;
};

/* -------------------------
   Context
------------------------- */

const UserLimitsContext = React.createContext<UserLimitsContextValue | null>(
  null
);

/* -------------------------
   Provider
------------------------- */

export function UserLimitsProvider({
  initialLimits,
  children,
}: {
  initialLimits: StudyUserLimits;
  children: React.ReactNode;
}) {
  const [limits, setLimits] = React.useState(initialLimits);

  const validators = React.useMemo(() => buildValidators(limits), [limits]);

  const value = React.useMemo(
    () => ({
      limits,
      validators,
      formatFileSize,
      setLimits,
    }),
    [limits, validators]
  );

  return (
    <UserLimitsContext.Provider value={value}>
      {children}
    </UserLimitsContext.Provider>
  );
}

/* -------------------------
   Hook
------------------------- */

export function useUserLimits() {
  const ctx = React.useContext(UserLimitsContext);
  if (!ctx) {
    throw new Error("useUserLimits must be used within a UserLimitsProvider");
  }
  return ctx;
}
